import React, { memo, useEffect, useCallback, useMemo, useRef } from "react";
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom";

import { useAxiomState, useAxiomEvent } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import AxiomLoader from "__AxiomComponents/AxiomUI/AxiomLoader/AxiomLoader.jsx";

import LandingPage from "../LandingPage/LandingPage";
import AllApps from "../AllApps/AllApps";
import ChangePassword from "../ChangePassword/ChangePassword";

import "./Home.scss";

import { checkMobileDevice } from "../homeUtils.js";

function Home(props) {
  const { appSettings, userInfo } = props;

  const [state, dispatch] = useAxiomState(
    {
      loading: true,
      apps: [],
      favouriteApps: [],
    },
    props.appName,
    props.appID,
    "Home",
    props.componentID,
  );

  useEffect(() => {
    if (appSettings && appSettings.theme && appSettings.theme.length > 0) {
      appSettings.theme.forEach((item) => {
        document.body.style.setProperty(item.key, item.value);
      });
    }
  }, [appSettings]);

  const showMessage = useAxiomEvent(props.appName, props.appID, "AxiomLayout", props.componentID);

  const { pathname } = useLocation();

  useEffect(() => {
    const isMobileDevice = checkMobileDevice(
      appSettings ? appSettings.forcefullyDisableMobileView : false,
    );
    if (!window.location.href.toString().includes("change-password")) {
      dispatch({ isMobileDevice: isMobileDevice }, "AxiomEvent", "GetDashboardAndApps");
    } else {
      dispatch({
        loading: false,
      });
    }
  }, [dispatch, appSettings]);

  const landingPageApps = state.favouriteApps;

  const favouriteApp = useCallback(
    (event, app) => {
      event.preventDefault();
      const isAlreayFavourite = state.favouriteApps.some((d) => d.name === app.name);
      if (!isAlreayFavourite) {
        dispatch({ loading: true });
        dispatch({ app }, "AxiomEvent", "FavouriteApp");
      }
    },
    [dispatch, state.favouriteApps],
  );

  const unfavouriteApp = useCallback(
    (event, app) => {
      event.preventDefault();
      dispatch({ loading: true });
      dispatch({ app }, "AxiomEvent", "UnfavouriteApp");
    },
    [dispatch],
  );

  const changePassword = useCallback(
    (oldPassword, newPassword) => {
      AxiomAPIs.updatepassword(newPassword, oldPassword, (cb) => {
        if (cb.status) {
          AxiomAPIs.logout(() => {});
          location.href = window.location.origin + window.location.pathname;
        } else {
          showMessage({
            snackOpen: true,
            snackMessage: cb.error.message,
            snackType: "error",
          });
        }
      });
    },
    [showMessage],
  );

  const logo = appSettings && appSettings.appLogo ? appSettings.appLogo : "images/hawkeye-logo.svg";

  const navigate = useNavigate();

  const hasSetInitialRoute = useRef(false);

  useEffect(() => {
    if (hasSetInitialRoute.current || state.loading) {
      return;
    }
    if (!window.location.href.toString().includes("change-password")) {
      hasSetInitialRoute.current = true;
      if (landingPageApps.length == 0) {
        navigate("/all-apps", { replace: true });
      } else {
        navigate("/favourite-apps", { replace: true });
      }
    }
  }, [landingPageApps, navigate, state.loading]);

  return (
    <div className="axiom-home">
      {state.loading && (
        <AxiomLoader
          background={"rgba(255, 255, 255, 0.4)"}
          fullScreen={true}
          block=".axiom-home"
        />
      )}
      <div className={`axiom-home-navbar ${pathname.substr(1)}`}>
        <Link to="/favourite-apps" className="axiom-home-logo" tabIndex="0">
          <img src={logo} className="axiom-home-logo-image" />
        </Link>
        {props.userInfo && (
          <div className="axiom-home-user">
            {props.userInfo.profilePhoto ? (
              <img className="axiom-home-user-avatar" src={props.userInfo.profilePhoto} />
            ) : (
              <span className="axiom-home-user-avatar">{props.userInfo.name.charAt(0)}</span>
            )}
            <div className="axiom-home-user-info">
              <div className="axiom-home-user-name">
                {props.userInfo ? props.userInfo.name : ""}
              </div>
              <div className="axiom-logout-pass-wrapper">
                <a onClick={props.logout} tabIndex="0" className="axiom-home-user-logout">
                  {AxiomAPIs.translate("Logout")}
                </a>
                {props.isShowChangePassword ? (
                  <div className="logout-pass-seprator-wrapper">
                    <div className="logout-pass-seprator">|</div>
                    <a
                      href="/apps/Home/#/change-password"
                      tabIndex="0"
                      className="axiom-home-user-change-pass"
                    >
                      {AxiomAPIs.translate("Change Password")}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
      <Routes>
        <Route
          path="/favourite-apps"
          element={
            <LandingPage
              loading={state.loading}
              appSettings={appSettings}
              apps={landingPageApps}
              unfavouriteApp={unfavouriteApp}
            />
          }
        />
        <Route
          path="/change-password"
          element={
            <ChangePassword
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              changePassword={changePassword}
              userInfo={userInfo}
            />
          }
        />
        <Route
          path="/all-apps"
          element={
            <AllApps
              loading={state.loading}
              apps={state.apps}
              favouriteApps={state.favouriteApps}
              favouriteApp={favouriteApp}
              unfavouriteApp={unfavouriteApp}
              appSettings={appSettings}
            />
          }
        />
        <Route path="/" element={<Navigate to="/favourite-apps" replace />} />
      </Routes>
      {appSettings &&
      appSettings.appCopyRight &&
      appSettings.appCopyRight.copyrightText &&
      appSettings.appCopyRight.isCopyRightText.toString() == "true" ? (
        <div className="copyright-home-bottom-container">
          {appSettings.appCopyRight.copyrightText}
        </div>
      ) : null}
    </div>
  );
}

export default memo(Home);
